import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import FlagIcon from './FlagIcon';
import styles from './LanguageDropdown.module.css';

/* 0.42.61 - `compact` drops the language NAME, leaving the flag and the
 * caret. The header asks for it when the menu items do not fit, so the
 * links get the width instead of the picker. The button keeps its
 * aria-label either way, so a screen reader is unaffected. */
export default function LanguageDropdown({ compact = false }) {
  const { language, setLanguage, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (nextLang) => {
    // 0.37.62 — When the current URL is a language-prefixed public
    // page (`/:lang/p/:slug`), pushing a new lang means editing
    // the lang segment IN THE URL — not just flipping the app
    // state — so external links stay canonical and a copy-paste
    // of the new URL carries the new language.
    //
    // For any other route (dashboard, admin, login, etc.) the
    // language dropdown behaves as before: flip the app state
    // via setLanguage (which also POSTs preferred_language to
    // the server when the visitor is signed in).
    const match = matchPath(
      { path: '/:lang/p/:slug', end: true },
      location.pathname,
    );
    if (match) {
      // Update the visitor's runtime language state (and
      // persist to server if signed in) — setLanguage does
      // both. Then rewrite the lang segment in the URL so a
      // copy-paste of the new URL carries the new language.
      setLanguage(nextLang);
      const nextPath = location.pathname.replace(
        /^\/[^/]+\/p\//,
        `/${nextLang}/p/`,
      );
      navigate(nextPath + location.search + location.hash, { replace: true });
    } else {
      setLanguage(nextLang);
    }
    setOpen(false);
  };

  if (!languages.length) return null;
  const currentLang = languages.find(l => l.code === language) || languages[0];

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
      >
        {/* 0.36.271 — Prefer the language's admin-configured `flag`
            (alpha-2 country code, e.g. 'gb' for English). When unset
            falls back to `code`, which FlagIcon then runs through
            its LANGUAGE_TO_COUNTRY map and finally the globe icon.
            0.36.738 — Trigger flag resized 26 -> 24 per admin, to
            match the menu options (already 24) so the flag doesn't
            visually jump between trigger and menu. */}
        <FlagIcon code={currentLang.flag || currentLang.code} size={24} />
        {/* 0.36.91 — Name wrapped in a span so CSS can hide it on
          * narrow viewports (where the hamburger is active and
          * horizontal space is at a premium). Flag stays visible
          * and the dropdown still works exactly the same. */}
        {!compact && (
          <span className={styles.triggerName}>{currentLang.name}</span>
        )}
        <span className={open ? `${styles.caret} ${styles.caretOpen}` : styles.caret} aria-hidden="true">
          {/* 0.36.736 — Was 10x8 (width x height in px); bumped to
              13x7 per spec. Base runs full 13 across the top,
              apex centered at x=6.5, dropping 7 to the bottom.
              viewBox matches the intrinsic 13x7 so the triangle
              renders sharp at any DPI. */}
          <svg width="13" height="7" viewBox="0 0 13 7" fill="currentColor">
            <path d="M 0 0 L 13 0 L 6.5 7 Z" />
          </svg>
        </span>
      </button>

      {open && (
        <div className={styles.menu} role="listbox">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`${styles.option} ${language === lang.code ? styles.active : ''}`}
              onClick={() => handleSelect(lang.code)}
              role="option"
              aria-selected={language === lang.code}
            >
              <FlagIcon code={lang.flag || lang.code} size={24} />
              <span className={styles.optionLabel}>{lang.name}</span>
              {language === lang.code && <span className={styles.check} aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
